import { type FC, JSXElementConstructor, ReactElement, ReactNode, useState } from "react";
import { Autocomplete, Button, Checkbox, Chip, TextField } from "@mui/material";
import { CheckBoxOutlineBlank, CheckBox } from "@mui/icons-material";
import FlexRow from "../FlexRow";
import { virtualizedList } from "./virtualizedList";

/** number of items before virtualizing list */
const VIRTUALIZATION_THRESHOLD = 100;

export type AutoCompleteOptions = {
  key: string;
  label: string;
  labelSuffix?: ReactNode | string;
  group?: string;
  /** Data in any form for use with externally supplied render hooks */
  extra?: any;
};

export const AutocompleteChips: FC<{
  options: AutoCompleteOptions[];
  selection: AutoCompleteOptions[];
  selectionHandler: (selection: AutoCompleteOptions[]) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  /* Use standard MUI button instead of "X" that shows on hover */
  buttonClear?: boolean;
  groupBy?: (options: AutoCompleteOptions) => string;
  chipIcon?: (
    options: AutoCompleteOptions
  ) => ReactElement<any, string | JSXElementConstructor<any>>;
}> = ({
  options,
  selection,
  selectionHandler,
  label,
  placeholder,
  helperText,
  buttonClear = true,
  groupBy,
  chipIcon,
}) => {
  const [inputValue, setInputValue] = useState("");

  const virtualized = !groupBy && options.length > VIRTUALIZATION_THRESHOLD;

  // for extending style with `sx` prop
  const styleX = {
    flexDirection: "column",
    width: "100%",
    "& .MuiInputBase-root": {
      paddingRight: "6px!important",
    },
    "& .MuiInputBase-input": {
      paddingRight: `${35 + (buttonClear && selection.length ? 65 : 0)}px!important`,
    },
    "& .MuiFormLabel-root": {
      lineHeight: "unset",
    },
    "& .MuiAutocomplete-endAdornment": {
      // move the dropdown toggle and clear button next to the input
      position: "absolute",
      top: "unset",
      right: "0px",
      margin: "unset",
      padding: "unset",
      bottom: "0px",
      marginBottom: "-12px",
      "& .MuiIconButton-root:hover": {
        // the clearIcon content is wrapped in IconButton, but we don't want the hover effect
        border: "1px solid transparent!important",
        bgcolor: "transparent!important",
      },
    },
  };

  // replace 'X' with a button if desired
  const clearIcon = buttonClear ? (
    <Button
      onClick={() => selectionHandler([])}
      variant="outlined"
      size="small"
      className="customClearBtn"
      sx={{
        fontSize: "12px",
        padding: "0px",
        marginRight: "-4px",
      }}
    >
      Clear
    </Button>
  ) : undefined;

  return (
    <Autocomplete
      disablePortal
      multiple
      disableCloseOnSelect
      slotProps={{
        popper: { keepMounted: true },

        listbox: {
          component: virtualized ? virtualizedList : undefined,
        },
      }}
      clearIcon={clearIcon}
      size="small"
      sx={styleX}
      options={options}
      value={selection}
      inputValue={inputValue}
      getOptionLabel={option => option.label}
      groupBy={groupBy}
      isOptionEqualToValue={(option, value) => {
        return option.key == value.key;
      }}
      onInputChange={(_, value, reason) => {
        if (reason != "reset") {
          // the search value was reset on each selection but
          // user may want to select >1 from filtered list
          setInputValue(value);
        }
      }}
      onChange={(_, selected) => {
        selectionHandler(selected);
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <li
            key={key}
            {...optionProps}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Checkbox
                size="small"
                icon={<CheckBoxOutlineBlank />}
                checkedIcon={<CheckBox />}
                style={{ marginLeft: -8 }}
                checked={selected}
              />
              <span>{option.label}</span>
            </div>
            <span>{option.labelSuffix}</span>
          </li>
        );
      }}
      renderInput={params => (
        <FlexRow fullWidth>
          <TextField
            {...params}
            fullWidth
            size="small"
            label={label}
            placeholder={placeholder}
            helperText={helperText}
          />
        </FlexRow>
      )}
      renderTags={(value, getTagProps) => {
        /* Padding to avoid overlap of CLEAR ALL btn */
        return (
          <FlexRow fullWidth wrapItems autoScrollY style={{ maxHeight: 100, padding: 5 }}>
            {value.map((option, index) => (
              <Chip
                icon={chipIcon ? chipIcon(option) : undefined}
                size="small"
                label={option.label}
                {...getTagProps({ index })}
                key={index}
              />
            ))}
          </FlexRow>
        );
      }}
    />
  );
};
